import type { ReactNode } from 'react';

import { useNavigate } from 'react-router-dom';
import { useMemo, useState, useContext, useCallback, createContext } from 'react';

import { useRouter } from 'src/routes/hooks';

import { showNotification } from 'src/lib/notifications';
import { useAuth } from 'src/features/auth/hooks/use-auth';
import { ProUpgradeDialog } from 'src/features/subscription/components';
import { useSubscriptionAccess } from 'src/features/subscription/hooks/use-subscription-access';
import { useSubscriptionStatus } from 'src/features/subscription/hooks/use-subscription-status';

interface SubscriptionContextProps {
  isPro: boolean;
  isActive: boolean;
  freeTrialCount: number;
  proEndDate: Date | null;
  loading: boolean;
  refreshSubscription: () => Promise<void>;
  checkFeatureAccess: (featureName: string) => Promise<boolean>;
  showProUpgradeDialog: (featureName: string, responseData?: unknown) => void;
  executeWithConfirmation: (featureName: string, callback: () => Promise<any>) => Promise<boolean>;
}

const SubscriptionContext = createContext<SubscriptionContextProps | null>(null);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider = ({ children }: SubscriptionProviderProps) => {
  const navigate = useNavigate();
  const router = useRouter();

  const { isAuthenticated } = useAuth();

  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  const { isPro, isActive, freeTrialCount, proEndDate, loading, refreshSubscription } =
    useSubscriptionStatus();

  const { checkFeatureAccess: checkFeatureAccessRequest } = useSubscriptionAccess();

  const showProUpgradeDialog = useCallback(
    (_featureName: string, _responseData?: unknown) => {
      setUpgradeDialogOpen(true);
    },
    [setUpgradeDialogOpen]
  );

  const checkFeatureAccess = useCallback(
    async (featureName: string) => {
      if (!isAuthenticated) {
        router.push('/login');
        return false;
      }

      try {
        const response = await checkFeatureAccessRequest(featureName);

        if (
          response.data?.requiresConfirmation &&
          (response.data?.freeTrialCount ?? 0) > 0
        ) {
          return true;
        }

        const canAccess = !response.errorCode;

        if (!canAccess) {
          showProUpgradeDialog(featureName, response.data);
          return false;
        }

        return true;
      } catch (error) {
        console.error(`Error checking access for feature ${featureName}:`, error);
        showNotification('Error checking feature access. Please try again.', 'error');
        return false;
      }
    },
    [checkFeatureAccessRequest, isAuthenticated, router, showProUpgradeDialog]
  );

  const executeWithConfirmation = useCallback(
    async (featureName: string, callback: () => Promise<any>): Promise<boolean> => {
      if (!isAuthenticated) {
        router.push('/login');
        return false;
      }

      try {
        const response = await checkFeatureAccessRequest(featureName);

        if (
          response.data?.requiresConfirmation &&
          (response.data?.freeTrialCount ?? 0) > 0
        ) {
          // The server takes the free trial when the feature produces a result.
          await callback();

          const remainingTrials = (response.data?.freeTrialCount ?? 1) - 1;
          if (remainingTrials === 0) {
            showNotification('⚡ Last free trial used! Subscribe for unlimited AI features', 'info');
          } else {
            showNotification(
              `✨ ${remainingTrials} free trial${remainingTrials !== 1 ? 's' : ''} remaining`,
              'info'
            );
          }

          await refreshSubscription();
          return true;
        }

        if (!response.errorCode) {
          await callback();
          return true;
        }

        showProUpgradeDialog(featureName, response.data);
        return false;
      } catch (error) {
        console.error(`Error executing with confirmation for feature ${featureName}:`, error);
        showNotification('An error occurred while checking feature access', 'error');
        return false;
      }
    },
    [
      checkFeatureAccessRequest,
      isAuthenticated,
      refreshSubscription,
      router,
      showProUpgradeDialog,
    ]
  );

  const handleUpgradeNow = useCallback(() => {
    setUpgradeDialogOpen(false);
    navigate('/subscription');
  }, [navigate]);

  const contextValue = useMemo(
    () => ({
      isPro,
      isActive,
      proEndDate,
      freeTrialCount,
      loading,
      refreshSubscription,
      checkFeatureAccess,
      showProUpgradeDialog,
      executeWithConfirmation,
    }),
    [
      checkFeatureAccess,
      executeWithConfirmation,
      freeTrialCount,
      isActive,
      isPro,
      loading,
      proEndDate,
      refreshSubscription,
      showProUpgradeDialog,
    ]
  );

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
      <ProUpgradeDialog
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        onUpgrade={handleUpgradeNow}
        freeTrialCount={freeTrialCount}
      />
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
